import {
  AnalyticsEvent,
  CreditScoreGrade,
  CreditScoreNoScore,
  CreditScoreRange,
  CreditScoreResult,
  CreditScoreSuccess,
  CreditScoreTile,
  TileStatus,
  Typenames,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import {
  CreditScoreCardProps,
  CreditScoreTileStep,
} from 'shared/components/vxl/cards/CreditScoreCard/CreditScoreCard';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { Maybe } from 'shared/utils/types';

interface CreditScoreTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.CREDIT_SCORE_CARD;
  props: CreditScoreCardProps;
}

interface CreditScoreTileWithAnalytics extends CreditScoreTile {
  creditScoreOnClickAnalytics?: AnalyticsEvent;
}

const creditScoreTile = (
  tile: CreditScoreTileWithAnalytics,
  dependencies: HomeCardDependencies,
): Maybe<CreditScoreTileNode> => {
  if (tile.status !== TileStatus.Loaded) {
    return null;
  }
  const { creditScoreTileClicked } = dependencies;
  const mappingResults = mapCreditScoreToProps(tile.creditScore);

  if (!mappingResults) {
    return null;
  }

  const { creditScore, delta, tagLine, steps, ranges, grade } = mappingResults;
  const gqlAnalytics = parseOptionalGQLAnalyticsEvent(
    tile.creditScoreOnClickAnalytics,
  ) ?? { name: '', properties: {} };

  const creditScoreCardProps: CreditScoreCardProps = {
    creditScore: creditScore,
    creditScoreDelta: delta,
    creditScoreDeltaTagline: tagLine,
    fillToStep: steps,
    handleCardPress: () => creditScoreTileClicked(gqlAnalytics),
    isDisabled: false,
    ranges: ranges,
    grade: grade,
  };

  const creditScoreNode: CreditScoreTileNode = {
    id: tile.id,
    name: VXLComponentId.CREDIT_SCORE_CARD,
    props: creditScoreCardProps,
  };
  return creditScoreNode;
};

function mapCreditScoreToProps(fragment: CreditScoreResult): {
  creditScore: string;
  delta: number | null;
  tagLine: string;
  steps: CreditScoreTileStep;
  ranges: CreditScoreRange[];
  grade: string;
} | null {
  if (fragment.__typename === Typenames.CreditScoreSuccess) {
    const fragmentAsScoreSuccess = fragment as CreditScoreSuccess;
    return {
      creditScore: fragmentAsScoreSuccess.score,
      delta: fragmentAsScoreSuccess.scoreDelta,
      tagLine: fragmentAsScoreSuccess.lastUpdated,
      steps: mapCreditScoreGradeToSteps(fragmentAsScoreSuccess.gradeEnum),
      ranges: fragmentAsScoreSuccess.ranges,
      grade: fragmentAsScoreSuccess.grade,
    };
  } else if (fragment.__typename === Typenames.CreditScoreNoScore) {
    const fragmentAsNoScore = fragment as CreditScoreNoScore;
    return {
      creditScore: fragmentAsNoScore.score,
      delta: null,
      tagLine: fragmentAsNoScore.description,
      steps: mapCreditScoreGradeToSteps(fragmentAsNoScore.gradeEnum),
      ranges: fragmentAsNoScore.ranges,
      grade: fragmentAsNoScore.grade,
    };
  }
  return null;
}

function mapCreditScoreGradeToSteps(
  grade: CreditScoreGrade,
): CreditScoreTileStep {
  switch (grade) {
    case CreditScoreGrade.Excellent:
      return 5;
    case CreditScoreGrade.Good:
      return 4;
    case CreditScoreGrade.Fair:
      return 3;
    case CreditScoreGrade.Poor:
      return 2;
    case CreditScoreGrade.VeryPoor:
      return 1;
    case CreditScoreGrade.NoScore:
      return 0;
  }
}

export default creditScoreTile;
